/*
 * Copyright (c) Nexign, JSC, 2022.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.22.0
 * @since 1.19.0
 */

import { useMemo } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { checkESTableList } from '@utils/checkESTable';

import {
    tableContext,
    tableDataLoadSizeSelector,
    tableFilterUrlSelector,
    tableListUrlSelector,
    tableMetaFilterSelector,
    tableMetaSortSelector,
    tableMetaUrlSelector,
} from '../selectors';
import { tableApi, useLazyGetTableListQuery } from '../store';
import type { ITableFilter, ITableSort } from '../types';

export const useTableFilterApi = () => {

    const dispatch = useDispatch();

    /* API */
    const [setFilterAndSort] = tableApi.endpoints.setTableFilterAndSort.useMutation();
    const [loadTableData] = useLazyGetTableListQuery();

    /* Selectors */
    const metaUrl = useSelector(tableMetaUrlSelector);
    const listUrl = useSelector(tableListUrlSelector);
    const filterAndSortUrl = useSelector(tableFilterUrlSelector);

    /* Контекст для основных операций*/
    const context = useSelector(tableContext);

    const filterSelector = useMemo(() => tableMetaFilterSelector(metaUrl), [metaUrl]);
    const filter = useSelector(filterSelector);
    const sort = useSelector(tableMetaSortSelector(metaUrl));

    const tableLoadSize = useSelector(tableDataLoadSizeSelector);

    const setFilterAndSortCallback = (
        filter?: ITableFilter, 
        sort?: ITableSort, 
        callback?: () => void, 
        skipLoadTableData?: boolean,
    ) => {
        if (!filterAndSortUrl) {
            // eslint-disable-next-line no-throw-literal
            throw '[Table]: incorrect table configuration. Set filterAndSort url in initialize method';
        }
        setFilterAndSort({ url: filterAndSortUrl, filter, sort, context })
            .unwrap()
            .then(() => {
                if (metaUrl && listUrl) {
                    /* Обновление внутреннего стора меты */
                    dispatch(tableApi.util.updateQueryData('getTableMeta', { url: metaUrl, context }, (draft) => {
                        draft.filterAndSort = { filter, sort };
                    }));

                    /* Запрос новых данных */
                    if (!skipLoadTableData) {
                        loadTableData({
                            url: listUrl,
                            from: 0,
                            size: tableLoadSize,
                            use_search_after: checkESTableList(listUrl),
                            context,
                        }).then(() => {
                            callback?.()
                        })
                    }
                } else {
                    // eslint-disable-next-line no-throw-literal
                    throw '[Table]: incorrect table configuration. Set meta and list url in initialize method';
                }
            });
    };

    return {
        /* Сбросить фильтры */
        drop: (callback?: () => void, skipLoadTableData?: boolean) => {
            setFilterAndSortCallback({}, sort, callback, skipLoadTableData);
        },
        /* Применение фильтра */
        filter: (filter?: ITableFilter, callback?: () => void) => {
            setFilterAndSortCallback(filter, sort, callback);
        },
        /* Применение сортировки */
        order: (sort: ITableSort, callback?: () => void) => {
            setFilterAndSortCallback(filter, sort, callback);
        },
    };
};
